import { Button } from "@/components/ui/button";
import { FileUploadInput } from "@/components/ui/file-upload-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  DOCUMENT_TYPES,
  useFileUploader,
  DocumentsResponse,
  Document
} from "@/components/features/FileUploader";
import { useDocumentTypes } from "@/hooks/useDocumentTypes";

interface LanguageCertificateFieldProps {
  profile: any;
  readOnly: boolean;
  loading: boolean;
}

export function LanguageCertificateField({
  profile,
  readOnly,
  loading
}: LanguageCertificateFieldProps) {
  const { t } = useTranslation(["talent", "common"]);
  const {
    uploadTalentProfileDocument,
    getTalentDocument,
    deleteFile,
    handleViewFile
  } = useFileUploader();
  const { data: documentTypes = [] } = useDocumentTypes();
  const [germanLanguageCertificateFile, setGermanLanguageCertificateFile] =
    useState<Document | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [loadingDocumentRead, setLoadingDocumentRead] = useState(false);

  const fetchGermanLanguageCertificateFile = useCallback(() => {
    const docType = documentTypes.find(
      (d) => d.id === DOCUMENT_TYPES.GERMAN_LANGUAGE_CERTIFICATE.id
    );
    if (!docType) return;

    setLoadingDocumentRead(true);
    getTalentDocument(profile.id || profile.talent_id, docType).then(
      (res: DocumentsResponse) => {
        if (res.success) {
          setGermanLanguageCertificateFile(res.data[0]);
        }
        setLoadingDocumentRead(false);
      }
    );
  }, [profile.id, profile.talent_id, getTalentDocument, documentTypes]);

  const handleFileUpload = async (file: File) => {
    if (!profile) return;
    const docType = documentTypes.find(
      (d) => d.id === DOCUMENT_TYPES.GERMAN_LANGUAGE_CERTIFICATE.id
    );
    if (!docType) return;

    setUploading(true);
    const result = await uploadTalentProfileDocument(
      file,
      docType,
      profile.id || profile.talent_id,
      {
        values: {
          talent_id: profile.id || profile.talent_id
        }
      }
    );
    if (result.success) {
      fetchGermanLanguageCertificateFile();
    }
    setUploading(false);
  };

  useEffect(() => {
    if ((profile?.id || profile?.talent_id) && documentTypes.length > 0) {
      fetchGermanLanguageCertificateFile();
    }
  }, [
    profile?.id,
    profile?.talent_id,
    fetchGermanLanguageCertificateFile,
    documentTypes
  ]);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <label className="text-sm font-medium">
          {t(
            "forms.sections.language_skills.fields.german_certificate_upload.label"
          )}
        </label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <HelpCircle className="h-4 w-4 text-gray-400" />
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {t(
                  "forms.sections.language_skills.fields.german_certificate_upload.help"
                )}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="flex items-center gap-2">
        <FileUploadInput
          currentFileName={germanLanguageCertificateFile?.file_name}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              handleFileUpload(file);
            }
          }}
          disabled={readOnly}
          loading={loading || loadingDocumentRead || uploading}
          onClearFile={async () => {
            if (germanLanguageCertificateFile) {
              const success = await deleteFile(
                "profiles",
                germanLanguageCertificateFile.id,
                germanLanguageCertificateFile.file_path
              );
              if (success) {
                fetchGermanLanguageCertificateFile();
              }
            }
          }}
        />
        {germanLanguageCertificateFile && (
          <Button
            disabled={readOnly}
            variant="outline"
            size="sm"
            onClick={() =>
              handleViewFile(
                "profiles",
                germanLanguageCertificateFile.file_path
              )
            }
          >
            {t("common:buttons.view")}
          </Button>
        )}
      </div>
    </div>
  );
}
